import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/lib/auth";
import { NAV_BY_ROLE, groupNav } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function Brand({ collapsed }) {
  return (
    <div className="flex items-center gap-3 px-2 py-3" data-testid="sidebar-brand">
      <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-cyan-400 to-blue-500 shadow-[0_0_24px_rgba(34,211,238,0.35)]">
        <Icons.Hexagon className="h-5 w-5 text-[#04111A]" strokeWidth={2.5} />
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <div className="text-1 font-semibold tracking-tight leading-tight">OmniDraftLab</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-3">Draft Intelligence</div>
        </div>
      )}
    </div>
  );
}

function NavItem({ item, collapsed }) {
  const Icon = Icons[item.icon] || Icons.Circle;
  const content = (
    <NavLink
      to={item.to}
      end={false}
      data-testid={`sidebar-nav-${item.key}`}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition",
          isActive
            ? "text-white bg-white/[0.08] ring-1 ring-cyan-300/25 shadow-[0_0_24px_rgba(34,211,238,0.18)]"
            : "text-white/65 hover:text-white hover:bg-white/[0.06]"
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              "absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-opacity",
              isActive ? "bg-cyan-300/80 opacity-100" : "opacity-0"
            )}
          />
          <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-cyan-300" : "text-white/70")} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={120}>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="glass-strong border-white/10">{item.label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return content;
}

export function Sidebar({ collapsed, onToggle, forceVisible = false }) {
  const { user, logout } = useAuth();
  const items = NAV_BY_ROLE[user?.role] || [];
  const grouped = groupNav(items);

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "relative z-20 flex-col shrink-0 border-r border-white/10 bg-white/[0.04] backdrop-blur-2xl transition-[width] duration-200",
        forceVisible ? "flex w-full h-full" : "hidden lg:flex",
        !forceVisible && (collapsed ? "w-[80px]" : "w-[272px]")
      )}
    >
      <Brand collapsed={collapsed} />
      {!forceVisible && (
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={onToggle}
          className="absolute -right-3 top-6 z-30 grid h-6 w-6 place-items-center rounded-full border border-white/10 bg-[color:var(--canvas-2)] text-white/70 hover:text-white"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <Icons.ChevronRight className="h-3.5 w-3.5" /> : <Icons.ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      )}

      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-2">
        {Object.entries(grouped).map(([group, list]) => (
          <div key={group} className="mb-4">
            {!collapsed && (
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-3">{group}</div>
            )}
            <div className="space-y-1">
              {list.map((item) => (
                <NavItem key={item.key} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="p-3">
        <div className="glass flex items-center gap-3 p-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-400/80 to-blue-500/80 grid place-items-center text-[#04111A] font-semibold">
            {(user?.full_name || "?").slice(0, 1)}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-1 text-sm font-medium truncate">{user?.full_name}</div>
              <div className="text-3 text-[11px] uppercase tracking-wider">{user?.role}</div>
            </div>
          )}
          {!collapsed && (
            <button
              data-testid="sidebar-logout"
              onClick={logout}
              title="Sign out"
              className="text-white/60 hover:text-white"
            >
              <Icons.LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
